'use client';
import React, { useMemo } from 'react';

import { Section, Container } from '@layout';
import { Card, CommandPalette, Heading, type CPCommand } from '@ui';
import { getDesignSystemNavItems } from '@components/design-system';
import MarkupSnippet, { markupCopyLabels } from '../markup-snippet/MarkupSnippet';

interface CommandPaletteDemoProps {
  sectionID?: string;
  classes?: string;
  containerId?: string;
  containerClasses?: string;
  isSpanish?: boolean;
}

const CommandPaletteDemo: React.FC<CommandPaletteDemoProps> = ({
  isSpanish = false,
  sectionID,
  classes = '',
  containerId,
  containerClasses,
}) => {
  const [open, setOpen] = React.useState(false);
  const t = (en: string, es: string) => (isSpanish ? es : en);

  const commands = useMemo<CPCommand[]>(
    () => getDesignSystemNavItems({ isSpanish, selfHref: '#cmdk' }),
    [isSpanish]
  );

  return (
    <>
      <Section className={classes} {...(sectionID ? { id: sectionID } : {})}>
        <Container containerId={containerId} containerClasses={containerClasses}>
          <Heading as='h3'>{t('Command Palette', 'Paleta de comandos')}</Heading>
          <p className='sub full'>
            {t(
              'Quickly jump to design system sections (same anchors as the slide-over nav).',
              'Salta rapido a secciones del sistema de diseño (los mismos anclajes que el menu lateral).'
            )}{' '}
            {t('Press', 'Presiona')} <kbd>⌘</kbd>/<kbd>Ctrl</kbd> + <kbd>K</kbd>{' '}
            {t(
              'or click the button to open the palette. Type to filter, use',
              'o haz clic en el boton para abrir la paleta. Escribe para filtrar, usa'
            )}{' '}
            <kbd>↑</kbd>/<kbd>↓</kbd>{' '}
            {t('to navigate, Enter to run, and Esc to close.', 'para navegar, Enter para ejecutar y Esc para cerrar.')}
          </p>
          <Card>
            <div className='row btn-row'>
              <CommandPalette
                commands={commands}
                open={open}
                onOpenChange={setOpen}
                showTrigger={true}
                triggerClassName='btn-secondary'
                label={
                  open
                    ? t('Close Command Palette', 'Cerrar paleta de comandos')
                    : t('Open Command Palette', 'Abrir paleta de comandos')
                }
              />
            </div>
          </Card>
        <MarkupSnippet
          {...markupCopyLabels(isSpanish)}
          code={`<div class="overlay" role="dialog" aria-modal="true">
  <div class="card modal-window size-md cmdk-panel">
    <header class="modal-header"><h3 class="modal-title">Search</h3></header>
    <div class="modal-body">
      <input type="search" aria-label="Command" />
    </div>
  </div>
</div>`}
        />
        </Container>
      </Section>
    </>
  );
};

export default CommandPaletteDemo;
